// src/features/records/utils/identityColors.js

/**
 * Produce a deterministic hue from a string seed.
 * Based on Avatar hashing logic so user-facing colors stay consistent.
 * @param {string} seed
 * @returns {number} hue value between 0 and 359
 */
const hashStringToHue = (seed = '') => {
  if (!seed) {
    return 210;
  }

  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }

  return Math.abs(hash) % 360;
};

const clampAlpha = (alpha) => {
  if (alpha == null) return 1;
  if (Number.isNaN(alpha)) return 1;
  return Math.min(1, Math.max(0, alpha));
};

/**
 * Get a vivid accent colour for a given identity.
 * @param {string} seed
 * @param {Object} [options]
 * @param {number} [options.saturation=70]
 * @param {number} [options.lightness=55]
 * @returns {string} hsl colour string
 */
export const getIdentityColor = (seed, { saturation = 70, lightness = 55 } = {}) => {
  const hue = hashStringToHue(seed);
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Get a softer translucent colour for backgrounds/accents.
 * @param {string} seed
 * @param {Object} [options]
 * @param {number} [options.saturation=78]
 * @param {number} [options.lightness=60]
 * @param {number} [options.alpha=0.18]
 * @returns {string} hsla colour string
 */
export const getIdentityTint = (
  seed,
  { saturation = 78, lightness = 60, alpha = 0.18 } = {}
) => {
  const hue = hashStringToHue(seed);
  return `hsla(${hue}, ${saturation}%, ${lightness}%, ${clampAlpha(alpha)})`;
};

/**
 * Convenience helper to fetch accent and tint variants.
 * @param {string} seed
 * @returns {{accent: string, tint: string}}
 */
export const getIdentityColorPair = (seed) => ({
  accent: getIdentityColor(seed),
  tint: getIdentityTint(seed)
});

/**
 * Get a richer set of colours for UI elements that need hover/background pairs.
 * @param {string} seed
 * @returns {{accent: string, tint: string, tintStrong: string}}
 */
export const getIdentityColorSet = (seed) => {
  const accent = getIdentityColor(seed);
  return {
    accent,
    tint: getIdentityTint(seed),
    tintStrong: getIdentityTint(seed, { alpha: 0.28, lightness: 55 })
  };
};

export default getIdentityColor;
